<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    theme="blue-background"
  >
    <template
      v-if="canGoBack"
      #header_start
    >
      <ButtonBack @click="navigateBack" />
    </template>
    <template #header_end><HeaderHelp /></template>

    <div class="kds-main-wrapper">
      <div class="kds-content-block">
        <p class="kds-typography-title-xlarge kds-typography-center">
          {{ title }}
        </p>
        <p class="kds-typography-paragraph-large kds-typography-center">
          {{ description }}
        </p>
      </div>
      <img
        class="insurance-covered-image"
        :src="illustrationImage"
        :width="illustrationImageDimensions.width"
        :height="illustrationImageDimensions.height"
        alt=""
      />
    </div>

    <template #actions>
      <BaseButton
        :text="$t('generic_action_continue')"
        data-qa="insurance-covered-cta"
        @click="navigateToNext"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'petite-vue-i18n'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useUserStore from '@shared/stores/useUserStore.js'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import useNavigation from '@eu/app/composables/useNavigation'
import ROUTE from '@eu/app/router/appModule/names'
import useOnboardingProgress from '@eu/app/composables/useOnboardingProgress.js'
import ButtonBack from '@shared/components/ButtonBack.vue'
import publicInsuranceImageDe from '@shared/assets/img/insurance_covered_public_de.png'
import publicInsuranceImageEn from '@shared/assets/img/insurance_covered_public_en.png'
import privateInsuranceImage from '@shared/assets/img/insurance_covered_private.svg'

const { t } = useI18n()
const corporateStore = useCorporateStore()
const userStore = useUserStore()
const { navigateNext, navigateBack, canGoBack } = useNavigation(
  ROUTE.INSURANCE_COVERED,
)
const { progress } = useOnboardingProgress()

const corporate = computed(() => corporateStore.corporate)
// treat 'not_set' as public insurance: assume those corporates cover costs.
const isPublicInsurance = computed(
  () => corporate.value?.insuranceType !== 'private',
)
const lang = computed(() => userStore.lang)
const publicInsuranceImage = computed(() =>
  lang.value.startsWith('de') ? publicInsuranceImageDe : publicInsuranceImageEn,
)

const title = computed(() =>
  isPublicInsurance.value
    ? t('eu__insurance_covered__title__public')
    : t('eu__insurance_covered__title__private'),
)

const description = computed(() =>
  isPublicInsurance.value
    ? t('eu__insurance_covered__description__public', {
        insurance: corporate.value?.title,
      })
    : t('eu__insurance_covered__description__private', {
        insurance: corporate.value?.title,
      }),
)

const illustrationImage = computed(() =>
  isPublicInsurance.value ? publicInsuranceImage.value : privateInsuranceImage,
)
const illustrationImageDimensions = computed(() =>
  isPublicInsurance.value
    ? { width: 335, height: 227 }
    : { width: 279, height: 211 },
)

const navigateToNext = () => {
  navigateNext()
}
</script>

<style scoped lang="scss">
.insurance-covered-image {
  display: block;
  width: 100%;
  max-width: 400px;
  height: auto;
  margin: 0 auto;
  border: 1px solid $kds-color-background-blue-accent;
  border-radius: $kds-space-1x;
}
</style>
